import { useAI } from '../ai-assist/provider/AiProvider';
import {
  IQuickShareMarkAsSentParams,
  useMarkShorUrlAsSentMutation,
} from '../ai-assist/queries/useShortUrlMutation';
import { GetQuickshareParams } from '../ai-assist/types';
import { useAuth } from '../provider/auth';
import { MarkAsSentOptions, VIDE0_ACTIVITY_TYPE } from '../types';
import { copyClipboard, isSafari } from '../utils/copy';

interface IProps {
  setShowModalShortUrlMarkAsSent: (
    showModalShortUrlMarkAsSent: boolean
  ) => void;
  handleShortUrlMarkAsSentSuccessCallback?: () => void;
  data: {
    generatedText: string;
    generatedUrl: string;
    generatedThumnail: string;
  };
  handleSubmitGeneratedContent?:
    | ((
        content: string,
        url?: string,
        thumbnail?: string,
        markAsSentConfirmed?: boolean,
        params?: GetQuickshareParams | undefined
      ) => Promise<void>)
    | ((
        content: string,
        url?: string,
        thumbnail?: string,
        markAsSentConfirmed?: boolean,
        params?: GetQuickshareParams | undefined
      ) => void);
  handleOnCopySuccess?:
    | ((
        content: string,
        url: string,
        thumbnail: string,
        markAsSentConfirmed: boolean,
        params?: GetQuickshareParams | undefined
      ) => Promise<void>)
    | ((
        content: string,
        url: string,
        thumbnail: string,
        markAsSentConfirmed: boolean,
        params?: GetQuickshareParams | undefined
      ) => void);
}

export const useShortUrlMarkAsSent = ({
  data,
  setShowModalShortUrlMarkAsSent,
  handleShortUrlMarkAsSentSuccessCallback,
  handleSubmitGeneratedContent,
  handleOnCopySuccess,
}: IProps) => {
  const { userData } = useAuth();
  const { params: aiParams } = useAI();
  const onSuccessCallback = () => {
    handleShortUrlMarkAsSentSuccessCallback?.();
    setShowModalShortUrlMarkAsSent(false);
  };

  // mutation
  const {
    isLoading: isLoadingShortUrlMarkAsSent,
    mutateAsync: quickShareMarkAsSentAsync,
  } = useMarkShorUrlAsSentMutation({
    onSuccessCallback,
    handleSubmitGeneratedContent,
    handleOnCopySuccess,
    data,
  });

  // mutation handler
  const onShortUrlMarkAsSentMutationHandler = async ({
    ...params
  }: IQuickShareMarkAsSentParams) => {
    const payload: IQuickShareMarkAsSentParams = {
      videoActivityType: VIDE0_ACTIVITY_TYPE.CRM_SHARE,
      ...params,
    };
    if (isSafari) {
      await copyClipboard('', '', {
        videoActivityType: VIDE0_ACTIVITY_TYPE.CRM_SHARE,
        ...params,
        ...aiParams,
        generatedText: data.generatedText,
      });
    }
    return await quickShareMarkAsSentAsync(payload);
  };

  // on button click
  const handleMarkAsSentFlow = (params: IQuickShareMarkAsSentParams) => {
    const markAsSent = !userData.isAutomotive
      ? MarkAsSentOptions.NEVER.value
      : userData.customer.markVideosAsSent === null
      ? MarkAsSentOptions.ASK_VERY_TIME.value
      : parseInt(userData.customer.markVideosAsSent.toString(), 10);

    // if markVideosAsSent set to NEVER -> do not mark as sent
    if (markAsSent === MarkAsSentOptions.NEVER.value) {
      return onShortUrlMarkAsSentMutationHandler({
        ...params,
      });
    }
    // if markVideosAsSent not set or set to always ask -> show modal
    if (markAsSent === MarkAsSentOptions.ASK_VERY_TIME.value) {
      return setShowModalShortUrlMarkAsSent(true);
    }
    // if markVideosAsSent is set to ALWAYS -> mark it as sent
    if (markAsSent === MarkAsSentOptions.ALWAYS.value) {
      return onShortUrlMarkAsSentMutationHandler({
        ...params,
      });
    }
  };

  return {
    handleMarkAsSentFlow,
    isLoadingShortUrlMarkAsSent,
    onShortUrlMarkAsSentMutationHandler,
  };
};
